import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { InfiniteLoadTrigger } from 'src/components/InfiniteLoadTrigger';
import { Meta } from 'src/components/Meta';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme, useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { fetchDashboardActivities } from 'src/models/activity/activity';
import { isProTrainer } from 'src/models/user/utils';
import { ActivityItem } from 'src/shared/ActivityItem/ActivityItem';
import { AdaptiveSideMenu } from 'src/shared/AdaptiveSideMenu/AdaptiveSideMenu';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { Identifier } from 'src/types/utilityTypes';

type ActivityTabType = 'clients' | 'user' | 'following' | 'all';

const ActivityPage = () => {
  const { session, user } = useSession();
  const { t } = useTranslation('translation', { keyPrefix: 'activity' });

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [searchParams, setSearchParams] = useSearchParams();

  const isUserProTrainer = isProTrainer(user);

  const showSitewide =
    (!session.privateUsers && session?.showActivity) || isUserProTrainer;

  const activityType =
    (searchParams.get('activity_type') as ActivityTabType) || 'user';

  const [currentTab, setCurrentTab] = useState(activityType);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['activities', currentTab, user?.id],
    queryFn: async ({ pageParam }) => {
      const lastItemId: Identifier | undefined =
        typeof pageParam === 'number' && pageParam === 1
          ? undefined
          : (pageParam as Identifier);
      const page = typeof pageParam === 'number' ? pageParam : 1;

      const response = await fetchDashboardActivities({
        activityType: currentTab,
        page: lastItemId ? undefined : page,
        perPage: 10,
        lastItemId,
      });
      return response;
    },
    enabled: !!user?.id,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 10 && lastPage.length === 0) return undefined;

      const lastItem = lastPage[lastPage.length - 1];
      if (!lastItem?.id) return undefined;

      return lastItem.id as Identifier;
    },
    initialPageParam: 1 as Identifier | number,
  });

  const handleTabChange = (newValue) => {
    setSearchParams(
      (pre) => {
        pre.set('activity_type', newValue);
        return pre;
      },
      { replace: true }
    );
    setCurrentTab(newValue);
  };

  const activities = data?.pages.flatMap((page) => page) || [];

  const tabs = [
    {
      value: 'clients',
      label: t('tabs.clients'),
      icon: icon({ name: 'users', style: 'regular' }),
      show: isUserProTrainer,
    },
    {
      value: 'user',
      label: t('tabs.me'),
      icon: icon({ name: 'user', style: 'regular' }),
      show: !isUserProTrainer,
    },
    {
      value: 'following',
      label: t('tabs.following'),
      icon: icon({ name: 'user-plus', style: 'regular' }),
      show: true,
    },
    {
      value: 'all',
      label: t('tabs.sitewide'),
      icon: icon({ name: 'globe', style: 'regular' }),
      show: showSitewide,
    },
  ].filter((tab) => tab.show);

  const pageTitle = t('title');

  return (
    <>
      <Meta title={pageTitle} brand={session?.brand} />
      <AppFrameConfig
        id="activity-id"
        containerMaxWidth={'xl'}
        title={pageTitle}
      />
      <Stack direction={mdDown ? 'column' : 'row'}>
        <AdaptiveSideMenu
          navItems={tabs}
          activeValue={activityType}
          onValueChange={(value) => handleTabChange(value)}
          collapsible={true}
          title={pageTitle}
          id="activity-id"
        />
        <Box sx={{ width: '100%', transition: 'all 0.3s ease' }}>
          <Stack spacing={3} pb={3}>
            {isLoading && (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Box p={4}>
                <Typography variant="body1" color="error" align="center">
                  {t('error')}
                </Typography>
              </Box>
            )}

            {!isLoading && !error && (
              <>
                {activities.length === 0 ? (
                  <Box p={4}>
                    <Typography variant="h5" align="center">
                      {t('empty')}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <>
                      {activities.map((activity) => (
                        <ActivityItem
                          key={activity.id}
                          activity={activity}
                          dateFormatter="long"
                          onlyCreatedAt={true}
                          showNote={false}
                        />
                      ))}
                      <InfiniteLoadTrigger
                        fetching={isFetchingNextPage}
                        onFetch={() => hasNextPage && fetchNextPage()}
                      >
                        {isFetchingNextPage && !isLoading && (
                          <CircularProgress size="small" />
                        )}
                      </InfiniteLoadTrigger>
                    </>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default ActivityPage;
